import {
  useTraderServiceBalanceQuery,
  useTraderServiceDetailsQuery,
  useTraderServiceGraphQuery,
  useTraderServiceInvestorsQuery,
  useTraderServiceManagementQuery,
  useTraderServicesQuery,
  useTraderServiceTransferFundsMutation,
  useTraderServiceUpdateScaMinimumMutation,
} from './api';
import { useDispatch, useSelector } from 'react-redux';
import { format, parse, subDays } from 'date-fns';
import {
  GraphChartType,
  GraphTimeframe,
  GraphTimeframeDayLength,
  Service,
  TraderService,
  TraderServiceChartProcessed,
  TransferPayload,
} from './types';
import { RootState } from '../store';
import { useIsAuthenticated, useUserSuccessFeeDiscount } from '../user/use';
import { useTitle } from 'util/title';
import { useTranslation } from 'react-i18next';
import {
  setChartTimeframe,
  setChartType,
  setIndexChartTimeframe,
} from './store';
import { useCallback, useMemo } from 'react';
import { formatMonthDayYear } from '../../views/Dashboard/components/MyDashboard/util';
import { isAccountRequired, whitelabel } from '../../whitelabel';
import { adjustDiscountFromBackend } from 'util/fee';
import { useInvestments } from 'apis/investment/use';

export function useTraderServices(): ReturnType<typeof useTraderServicesQuery> {
  const isAuthenticated = useIsAuthenticated();
  return useTraderServicesQuery(undefined, {
    skip: !isAuthenticated,
  });
}

export function useIsServiceOwner(serviceId: string): boolean | undefined {
  const isAuthenticated = useIsAuthenticated();

  const { data: traderServices } = useTraderServices();
  return (
    isAuthenticated &&
    traderServices?.some((s: TraderService) => s.serviceId === serviceId)
  );
}

export function useFirstOwnedService(): TraderService | null {
  const { data: traderServices } = useTraderServices();
  return (traderServices && traderServices[0]) || null;
}

export const useTraderServiceInvestors = useTraderServiceInvestorsQuery;

export const useTraderServiceManagement = useTraderServiceManagementQuery;

export const useServiceDetails = (
  serviceId: string,
  options?,
): ReturnType<typeof useTraderServiceDetailsQuery> => {
  const isAuthenticated = useIsAuthenticated();
  const doNotLoadServicesForUnauth = !isAuthenticated && isAccountRequired;
  return useTraderServiceDetailsQuery(serviceId, {
    ...(options || {}),
    skip: options?.skip || doNotLoadServicesForUnauth,
  });
};

export const useTraderServiceBalance = useTraderServiceBalanceQuery;

export function useTraderServiceUpdateMinimum(
  serviceId: string,
): [(minimum: string) => Promise<void>, { isLoading: boolean }] {
  const [update, { isLoading }] = useTraderServiceUpdateScaMinimumMutation();
  const { isFetching: isLoadingManagement } =
    useTraderServiceManagement(serviceId);
  return [
    async (minimum) => {
      await update({ minimum, serviceId }).unwrap();
    },
    { isLoading: isLoading || isLoadingManagement },
  ];
}

export function useTraderServiceTransferFunds(
  serviceId: string,
): [(payload: TransferPayload) => Promise<void>, { isLoading: boolean }] {
  const [update, { isLoading }] = useTraderServiceTransferFundsMutation();
  const { isFetching: isLoadingManagement } =
    useTraderServiceBalance(serviceId);
  return [
    async (payload) => {
      await update({ ...payload, serviceId }).unwrap();
    },
    { isLoading: isLoading || isLoadingManagement },
  ];
}

export function useTraderServiceTitle(
  translationKey: string,
  serviceId: string,
): void {
  const { data: service } = useServiceDetails(serviceId);
  const { t } = useTranslation('pages');

  useTitle(
    service
      ? t(translationKey, { serviceName: service.name })
      : t('trading-services'),
  );
}

export function useChartConfig({ isIndex }: { isIndex: boolean }): {
  chartType: GraphChartType;
  chartTimeframe: GraphTimeframe;
  setChartType: (v: GraphChartType) => void;
  setChartTimeframe: (v: GraphTimeframe) => void;
} {
  const { chartType, chartTimeframe, indexChartTimeframe } = useSelector(
    (store: RootState) => store.service,
  );
  const dispatch = useDispatch();
  return {
    chartType: chartType || GraphChartType.pnl_pct_compound,
    chartTimeframe:
      (isIndex ? indexChartTimeframe : chartTimeframe) ||
      GraphTimeframe['180d'],
    setChartType: (v) => dispatch(setChartType(v)),
    setChartTimeframe: (v) =>
      isIndex
        ? dispatch(setIndexChartTimeframe(v))
        : dispatch(setChartTimeframe(v)),
  };
}

export function useChartData({
  service,
  chartType,
  chartTimeframe,
}: {
  chartType: GraphChartType;
  chartTimeframe: GraphTimeframe;
  service: Service;
}) {
  const { data, isLoading, isFetching, isError } = useTraderServiceGraphQuery({
    id: service.id,
    period: chartTimeframe,
    chart: chartType,
  });

  const chartData = useMemo<TraderServiceChartProcessed>(() => {
    const chart = { ...(data?.data || {}) };
    const now = new Date();
    for (
      let i = -1 * (GraphTimeframeDayLength[chartTimeframe] || 0);
      i < 0;
      i++
    ) {
      const key = format(subDays(now, -1 * i), 'yyyy-MM-dd');
      if (typeof chart[key] !== 'undefined') {
        break;
      } else {
        chart[key] = 0;
      }
    }

    const dates = Object.entries(chart).sort(([a], [b]) => a.localeCompare(b));

    if (chartType === GraphChartType.pnl_pct_compound && dates.length) {
      // Prepend previous date as 0
      const [firstDate, firstValue] = dates[0];
      if (firstValue !== 0) {
        const previousDate = format(
          subDays(parse(firstDate, 'yyyy-MM-dd', Date.now()), 1),
          'yyyy-MM-dd',
        );
        dates.unshift([previousDate, 0]);
      }
    } else if (chartType === GraphChartType.sbt_ssc && dates.length) {
      // add pending to last day
      dates[dates.length - 1][1] += service.pending;
    }

    const graph = dates?.reduce((acc, [date, value]) => {
      const dateObj = parse(date, 'yyyy-MM-dd', Date.now());
      const label = formatMonthDayYear(dateObj);

      return [
        ...acc,
        {
          x: label,
          date: dateObj,
          y: value,
        },
      ];
    }, []);

    return {
      summary: data?.summary,
      percentDiff: [GraphChartType.investors, GraphChartType.sbt_ssc].includes(
        chartType,
      )
        ? data?.summaryPct
        : undefined,
      migrationDate: data?.migration_date,
      migrationIndex: dates.findIndex(([x]) => x === data?.migration_date),
      data: graph,
    };
  }, [data?.data, service.pending]);

  return { data: chartData, isLoading, isFetching, isError };
}

export function useGetSuccessFeeDiscount() {
  const discount = useUserSuccessFeeDiscount();
  const { data: investments, isLoading } = useInvestments();

  const { data: myServices } = useTraderServices();

  return useCallback(
    (
      service: Pick<Service, 'ownerSfDiscount' | 'successFee' | 'id'> & {
        zglySuccessFee?: number;
      },
    ) => {
      if (isLoading || !service.successFee) return 0;
      if (myServices?.find((s) => s.serviceId === service.id)) {
        return adjustDiscountFromBackend(
          100,
          service.successFee,
          service.zglySuccessFee || whitelabel.zignalySuccessFee,
        );
      }

      const investment = investments?.find((x) => x.serviceId === service.id);

      return adjustDiscountFromBackend(
        +investment?.ownerSfDiscount || discount,
        service.successFee,
        service.zglySuccessFee || whitelabel.zignalySuccessFee,
      );
    },
    [investments, isLoading],
  );
}

export function useSuccessFeeDiscount({
  ownerSfDiscount,
  successFee,
  id,
  zglySuccessFee,
}: Service) {
  const getSuccessFeeDiscount = useGetSuccessFeeDiscount();
  return useMemo(
    () =>
      getSuccessFeeDiscount({
        ownerSfDiscount,
        successFee,
        id,
        zglySuccessFee,
      }),
    [getSuccessFeeDiscount],
  );
}
